import {
  Box,
  Button,
  Icon,
  IconButton,
  Menu,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React from "react";
import {
  CloseIcon,
  MicDarkIcon,
  MicIcon,
  ProfileBlob,
  OrbitIcon,
  StopIcon,
  ResetIcon,
  SendIcon,
} from "../assets/images";
import { Formik, Field } from "formik";
import Aos from "aos";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { env } from "../env";
import {
  generateConsultationNote,
  generateSOAPNote,
} from "../services/transcription/transcription-service";
import { Jodit } from "jodit-react";
import FormikJoditEditor from "../component/FormikJoditEditor";
import FormikInput from "../component/FormikInput";
import FormikSelect from "../component/FormikSelect";
import TranscriptionDialog from "../component/TranscriptionDialog";
import { Instructions } from "../shared/enums/instructions";
import { ChatTypes } from "../shared/enums/chat-types";
import { IProcessedResponse } from "../models/processes-response";
import { toast } from "react-toastify";

declare global {
  interface Window {
    env: any;
  }
}

const SPEECH_KEY = env.REACT_APP_SPEECH_KEY;
const SPEECH_REGION = env.REACT_APP_SPEECH_REGION;
const ENABLE_SPEECH = window.env?.ENABLE_SPEECH;

console.log(ENABLE_SPEECH);

const Recognition = () => {
  const transcriptRef = React.useRef("");
  const speechConfig: any = React.useRef();
  const audioConfig: any = React.useRef();
  const recognizer: any = React.useRef();

  const [timer, setTimer] = React.useState(0);
  const [openEditor, setOpenEditor] = React.useState(false);
  const [openSoapNoteDialog, setOpenSoapNoteDialog] = React.useState(false);
  const [isListening, setIsListening] = React.useState(false);
  const [myTranscript, setMyTranscript] = React.useState("");
  const [recognizingTranscript, setRecognizingTranscript] = React.useState("");
  const [outputString, setOutputString] = React.useState("");
  const [transcriptedOutput, setTranscriptedOutput] =
    React.useState<IProcessedResponse | null>(null);
  const [chatType, setChatType] = React.useState(
    Instructions.insSoapNote as string
  );

  const [anchorElChat, setAnchorElChat] = React.useState<null | HTMLElement>(
    null
  );
  const openChat = Boolean(anchorElChat);

  const initialState = {
    transcriptedOutput: transcriptedOutput,
    myTranscript: myTranscript,
  };

  const timeOutCallback = React.useCallback(
    () => setTimer((currTimer) => currTimer + 1),
    []
  );

  React.useEffect(() => {
    let timeoutId: string | number | NodeJS.Timeout | undefined;
    if (isListening) timeoutId = setTimeout(timeOutCallback, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [timer, isListening, timeOutCallback]);

  React.useEffect(() => {
    Aos.init({
      once: true,
      duration: 1250,
      disable: "mobile",
    });
  }, []);

  const captureRecognizingTranscript = (event: any) => {
    const result = event.result;
    if (result.reason === sdk.ResultReason.RecognizingSpeech) {
      console.log("result.text", result.text);
      setRecognizingTranscript(result.text);
    }
  };

  const captureRecognizedTranscript = (event: any) => {
    const result = event.result;

    if (result.reason === sdk.ResultReason.RecognizedSpeech) {
      let res = result.text;
      // if (res.toLowerCase() === "stop listening.") {
      //   stopListening();
      // } else {
      transcriptRef.current += (transcriptRef.current ? "\n" : "") + res;
      //}
      console.log("transcriptRef.current", transcriptRef.current);
      setMyTranscript(transcriptRef.current);
    }
  };

  React.useEffect(() => {
    speechConfig.current = sdk.SpeechConfig.fromSubscription(
      SPEECH_KEY,
      SPEECH_REGION
    );
    speechConfig.current.speechRecognitionLanguage = "en-US";

    audioConfig.current = sdk.AudioConfig.fromDefaultMicrophoneInput();
    console.log(audioConfig.current);
    recognizer.current = new sdk.SpeechRecognizer(
      speechConfig.current,
      audioConfig.current
    );

    recognizer.current.recognized = (s: any, e: any) =>
      captureRecognizedTranscript(e);

    recognizer.current.recognizing = (s: any, e: any) =>
      captureRecognizingTranscript(e);

    // recognizer.current.startContinuousRecognitionAsync(() => {
    //   console.log("Speech recognition started.");
    //   setIsListening(true);
    // });

    return () => {
      recognizer.current?.stopContinuousRecognitionAsync(() => {
        setIsListening(false);
      });
    };
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  const handleChatChange = (event: SelectChangeEvent) => {
    setChatType(event.target.value as string);
  };

  const handleClickTranscription = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const isSpeechEnabled = ENABLE_SPEECH === "true";

    if (isSpeechEnabled) {
      setAnchorElChat(event.currentTarget);
    } else {
      toast.error(
        "AI services has been disabled for temporary basis, please contact admin."
      );
    }
  };

  const handleClickVideoCall = (event: React.MouseEvent<HTMLButtonElement>) => {
    const isSpeechEnabled = ENABLE_SPEECH === "true";

    if (isSpeechEnabled) {
      window.location.replace("/ai-assistant/videocall");
    } else {
      toast.error(
        "AI services has been disabled for temporary basis, please contact admin."
      );
    }
  };

  const handleCloseChat = (forceClose = false) => {
    if (forceClose) {
      setAnchorElChat(null);
    }
  };

  const handleOpenEditor = () => setOpenEditor(true);
  const handleCloseEditor = () => setOpenEditor(false);

  const handleOpenSoapNoteDialog = () => setOpenSoapNoteDialog(true);
  const handleCloseSoapNoteDialog = () => setOpenSoapNoteDialog(false);

  const resetListening = () => {
    transcriptRef.current = "";
    recognizer.current?.stopContinuousRecognitionAsync(() => {
      setIsListening(false);
      setRecognizingTranscript("");
      setMyTranscript("");
      setOutputString("");
      setTimer(0);
    });
  };

  const resumeListening = () => {
    if (!isListening) {
      setIsListening(true);
      recognizer.current?.startContinuousRecognitionAsync();
    }
  };

  const stopListening = () => {
    setIsListening(false);
    recognizer.current?.stopContinuousRecognitionAsync();
  };

  const processTranscriptwithAI = (instruction: string) => {
    if (Instructions.insSoapNote === instruction) {
      generateSOAPNote(instruction, myTranscript)
        .then((response: any) => {
          setTranscriptedOutput(response.data);
          handleOpenSoapNoteDialog();
        })
        .catch((error: any) =>
          console.error("Error sending message to backend:", error)
        );
      // } else if (Instructions.insConsultation === instruction) {
    } else {
      generateConsultationNote(instruction, myTranscript)
        .then((response: any) => {
          setOutputString(response.data);
          handleOpenEditor();
        })
        .catch((error: any) =>
          console.error("Error sending message to backend:", error)
        );
    }
  };

  return (
    <>
      <div className="ai-wrapper">
        <div className="title-block" data-aos="fade-right">
          <Typography variant="h1">AI Assistant</Typography>
        </div>
        <div className="blob-wrap" data-aos="zoom-in">
          <img src={ProfileBlob} alt="Profile" />
        </div>
        <div className="fab-wrap">
          <IconButton
            color="primary"
            className="icon-btn"
            size="large"
            onClick={handleClickTranscription}
          >
            <img src={MicIcon} alt="Icon" />
          </IconButton>

          {/* <IconButton
            color="primary"
            className="icon-btn"
            size="large"
            onClick={handleClickVideoCall}
          >
            <img src={SendIcon} alt="Icon" />
          </IconButton> */}
        </div>
      </div>

      <Formik
        initialValues={initialState}
        onSubmit={() => console.log(transcriptedOutput)}
        validateOnBlur={true}
        validateOnChange={true}
        enableReinitialize={true}
      >
        {(props: { setFieldValue: any; handleSubmit: any }) => {
          const { setFieldValue, handleSubmit } = props;
          return (
            <form onSubmit={handleSubmit} noValidate>
              <Menu
                id="chat-menu"
                anchorEl={anchorElChat}
                open={openChat}
                onClose={() => handleCloseChat(false)}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                sx={{ maxWidth: "sm" }}
                className="chat-menu"
                disableScrollLock={true}
              >
                <div className="menu-head">
                  <Typography variant="h4" className="menu-title">
                    AI Assistant
                  </Typography>
                  <IconButton
                    className="icon-btn btn-close"
                    onClick={() => handleCloseChat(true)}
                  >
                    <img src={CloseIcon} alt="Icon" />
                  </IconButton>
                </div>
                <div className="cta-wrap">
                  <Button
                    variant="contained"
                    className={timer <= 0 && !myTranscript ? "disable" : ""}
                    onClick={resetListening}
                    disabled={timer <= 0 && !myTranscript}
                  >
                    <Icon>
                      <img
                        style={{ marginTop: "3px" }}
                        src={ResetIcon}
                        alt="Icon"
                      />
                    </Icon>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    className="active"
                    onClick={resumeListening}
                  >
                    <Icon>
                      <img src={MicDarkIcon} alt="Icon" />
                    </Icon>
                    Start
                  </Button>
                  <Button
                    variant="contained"
                    className={timer <= 0 ? "disable" : ""}
                    onClick={stopListening}
                    disabled={timer <= 0}
                  >
                    <Icon>
                      <img src={StopIcon} alt="Icon" />
                    </Icon>
                    Stop
                  </Button>
                </div>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1rem",
                      color: "#1976d2",
                    }}
                  >
                    {formatTime(timer)}
                  </Typography>
                </Box>

                <div className="chat-body">
                  <Field
                    type="text"
                    name="myTranscript"
                    id="myTranscript"
                    className="st-textarea"
                    maxLength={10000}
                    rows={10}
                    multiline
                    onKeyDown={(e: { stopPropagation: () => any }) =>
                      e.stopPropagation()
                    }
                    onChange={(e: any) => {
                      transcriptRef.current = e?.target?.value;
                      setFieldValue("myTranscript", e?.target?.value);
                      setMyTranscript(e?.target?.value);
                    }}
                    component={FormikInput}
                  />
                  <div className="voice-block">
                    <label>{recognizingTranscript}</label>
                  </div>
                </div>
                <div className="chat-footer">
                  <div className="f-inner">
                    <Field
                      name="chatType"
                      id="chatType"
                      value={chatType}
                      onChange={handleChatChange}
                      MenuProps={{
                        className: "ai-select-menu",
                        anchorOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                      }}
                      options={Object.entries(ChatTypes).map(
                        ([key, value]) => ({
                          title: key,
                          value,
                        })
                      )}
                      component={FormikSelect}
                    />

                    <IconButton
                      className={
                        !!myTranscript ? "icon-btn btn-send" : "d-none"
                      }
                      color="primary"
                      size="large"
                      disabled={!myTranscript}
                      onClick={() => processTranscriptwithAI(chatType)}
                    >
                      <img src={OrbitIcon} alt="Icon" />
                    </IconButton>
                  </div>
                </div>
              </Menu>

              {openEditor && (
                <Field
                  name="outputString"
                  id="outputString"
                  open={openEditor}
                  handleClose={handleCloseEditor}
                  value={outputString}
                  onChange={(value: any) => {
                    if (!Jodit.modules.Helpers.stripTags(value))
                      setFieldValue("outputString", null);
                    else setFieldValue("outputString", value);
                  }}
                  component={FormikJoditEditor}
                />
              )}

              {openSoapNoteDialog && (
                <Field
                  name="transcriptedOutput"
                  id="transcriptedOutput"
                  open={openSoapNoteDialog}
                  handleClose={handleCloseSoapNoteDialog}
                  value={transcriptedOutput}
                  onChange={(value: any) => {
                    setFieldValue("transcriptedOutput", value);
                  }}
                  component={TranscriptionDialog}
                />
              )}
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default Recognition;
